import { PlusOutlined } from "@ant-design/icons";
import { Avatar, Button, Col, Divider, Flex, Select, Typography } from "antd";
import { DefaultOptionType } from "antd/es/select";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { setAddClientView } from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import { getRandomAvatarColor } from "../../utils/ui.utils";
import styles from "./createnotemodal.module.scss";

const ClientSelector = () => {
  const componentName = "createnotemodal";
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileView();
  const { isEditNote } = useAppSelector((state) => state.appState);
  const { clients } = useAppSelector((state) => state.client);
  const { recordingDetail } = useAppSelector((state) => state.recordings);
  const { referIndividualBy } = useSpecialityReference();
  const clientOptions = useMemo(() => {
    return clients.map((client) => {
      const formattedDob = client.date_of_birth ? dayjs(client.date_of_birth).format("MM/DD/YYYY") : "";
      const derivedAgeFromDob =
        client.date_of_birth && formattedDob ? dayjs().diff(dayjs(client.date_of_birth), "year") : null;
      const rawAge =
        typeof client.age === "number" ? client.age : client.age ? Number.parseInt(client.age.toString(), 10) : null;
      const displayAge = formattedDob ? derivedAgeFromDob : rawAge;
      const searchTokens = [client.name];

      if (client.mrn) {
        searchTokens.push(client.mrn);
      }
      if (formattedDob) {
        searchTokens.push(formattedDob);
      } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
        searchTokens.push(displayAge.toString());
      }

      const secondaryDetails: string[] = [];
      if (formattedDob) {
        secondaryDetails.push(`DOB: ${formattedDob}`);
      } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
        secondaryDetails.push(`Age: ${displayAge}`);
      }
      if (client.mrn) {
        secondaryDetails.push(`MRN: ${client.mrn}`);
      }

      return {
        label: (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>
              {client.name}{" "}
              {!client.is_owned && client.owner_name && (
                <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
                  - Shared by {client.owner_name}
                </span>
              )}
            </span>
            {secondaryDetails.length > 0 && (
              <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
                {secondaryDetails.join(" · ")}
              </span>
            )}
          </div>
        ),
        value: client.id,
        optionLabel: client.name,
        searchKey: searchTokens.join(" ").toLowerCase(),
      };
    });
  }, [clients]);

  return (
    <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
      <Typography.Text>{referIndividualBy}:</Typography.Text>
      <Select
        value={recordingDetail?.client_id}
        listHeight={isMobile ? 66 : 256}
        onChange={(value) => {
          dispatch(
            setRecordingDetail({
              ...recordingDetail,
              client_id: value,
            }),
          );
        }}
        showSearch
        placeholder={`Select a ${referIndividualBy.toLowerCase()}`}
        optionFilterProp="children"
        filterOption={(input, option) => {
          const normalizedInput = input.trim().toLowerCase();
          if (!normalizedInput) {
            return true;
          }
          if (!option) {
            return false;
          }
          const optionWithSearchKey = option as DefaultOptionType & { searchKey?: string };
          if (optionWithSearchKey.searchKey?.includes(normalizedInput)) {
            return true;
          }
          const { label } = optionWithSearchKey;
          const labelText = typeof label === "string" ? label : typeof label === "number" ? String(label) : "";
          if (labelText) {
            return labelText.toLowerCase().includes(normalizedInput);
          }
          return false;
        }}
        labelRender={(client) => {
          const selectedClient = clients.find((c) => c.id === recordingDetail?.client_id);
          if (!selectedClient) return null;

          return selectedClient.name;
        }}
        optionRender={(client) => {
          const clientName = client.data?.optionLabel || "";
          const initials = clientName
            .split(" ")
            .filter((word) => word.length > 0)
            .map((word) => word.charAt(0).toUpperCase())
            .slice(0, 2)
            .join("");

          return (
            <Flex gap={10} align="center">
              <Avatar
                style={{
                  backgroundColor: getRandomAvatarColor(clientName),
                }}
                size="small"
              >
                {initials}
              </Avatar>
              <Typography.Text>{client.label}</Typography.Text>
            </Flex>
          );
        }}
        dropdownRender={(menu) => (
          <div className="client">
            {menu}
            {!isEditNote ? (
              <>
                <Divider style={{ margin: "4px 0" }} />
                <Flex gap={10}>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      dispatch(setAddClientView(true));
                    }}
                  >
                    Add {referIndividualBy}
                  </Button>
                </Flex>
              </>
            ) : null}
          </div>
        )}
        className={`${styles[`${componentName}__modal-row-field`]}`}
        options={clientOptions}
      />
    </Col>
  );
};

export default ClientSelector;
